import * as Sentry from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';
import { captureException as capturePostHogException } from '$lib/analytics/posthog';

// Detect mobile devices for reduced sampling
const isMobile =
	typeof navigator !== 'undefined' &&
	/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// Sentry sampling rates - reduced on mobile for performance
const SENTRY_TRACES_SAMPLE_RATE = isMobile ? 0.1 : 1.0; // 10% on mobile, 100% on desktop
const SENTRY_SESSION_REPLAY_RATE = isMobile ? 0 : 0.1; // Disabled on mobile (heavy rrweb library)
const SENTRY_ERROR_REPLAY_RATE = isMobile ? 0 : 1.0; // Disabled on mobile

// Error source identifiers for analytics correlation
const ERROR_SOURCES = {
	SVELTE_ERROR_HANDLER: 'handleError',
	WINDOW_ERROR: 'window.onerror',
	UNHANDLED_REJECTION: 'unhandledrejection'
} as const;

// Initialize Sentry for client-side error tracking
// DSN is read from env at runtime to avoid build errors
const SENTRY_DSN = import.meta.env.PUBLIC_SENTRY_DSN;

if (SENTRY_DSN) {
	Sentry.init({
		dsn: SENTRY_DSN,
		environment: import.meta.env.MODE,
		tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
		replaysSessionSampleRate: SENTRY_SESSION_REPLAY_RATE,
		replaysOnErrorSampleRate: SENTRY_ERROR_REPLAY_RATE,
		integrations: [Sentry.replayIntegration()],
		// Don't send errors in development
		enabled: import.meta.env.PROD
	});
}

// Custom error handler that sends to both Sentry and PostHog
const customErrorHandler: HandleClientError = ({ error, event }) => {
	// Send to PostHog for correlation with analytics
	capturePostHogException(error, {
		url: event.url.pathname,
		source: ERROR_SOURCES.SVELTE_ERROR_HANDLER
	});
	return {
		message: 'An unexpected error occurred'
	};
};

// Wrap with Sentry if initialized, otherwise use custom handler directly
export const handleError: HandleClientError = SENTRY_DSN
	? Sentry.handleErrorWithSentry(customErrorHandler)
	: customErrorHandler;

// Global error handlers for uncaught errors
if (typeof window !== 'undefined') {
	window.addEventListener('error', (event) => {
		capturePostHogException(event.error ?? event.message, {
			source: ERROR_SOURCES.WINDOW_ERROR
		});
	});

	window.addEventListener('unhandledrejection', (event) => {
		capturePostHogException(event.reason, {
			source: ERROR_SOURCES.UNHANDLED_REJECTION
		});
	});
}
